import * as THREE from 'three';
import { QualityLevel } from './EffectsManager';

export interface InteractionEvent {
  type: 'hover' | 'click' | 'select' | 'camera_transition';
  object?: THREE.Object3D;
  position?: THREE.Vector3;
  intensity?: number;
}

interface SelectionOutline {
  object: THREE.Object3D;
  outlineMesh: THREE.Mesh;
  startTime: number;
  duration: number;
}

interface HoverEffect {
  object: THREE.Object3D;
  originalScale: THREE.Vector3;
  targetScale: THREE.Vector3;
  startTime: number;
  duration: number;
}

interface ClickRipple {
  position: THREE.Vector3;
  mesh: THREE.Mesh;
  startTime: number;
  duration: number;
}

export class InteractionEffects {
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private quality: QualityLevel = 'medium';
  
  // Effect collections
  private selectionOutlines: Map<string, SelectionOutline> = new Map();
  private hoverEffects: Map<string, HoverEffect> = new Map();
  private clickRipples: ClickRipple[] = [];
  
  // Materials
  private outlineMaterial: THREE.ShaderMaterial;
  private rippleMaterial: THREE.ShaderMaterial;
  private glowMaterial: THREE.ShaderMaterial;
  
  // Animation state
  private time = 0;
  
  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.renderer = renderer;
    
    this.initializeMaterials();
  }
  
  private initializeMaterials(): void {
    // Selection outline material
    this.outlineMaterial = new THREE.ShaderMaterial({
      vertexShader: this.getOutlineVertexShader(),
      fragmentShader: this.getOutlineFragmentShader(),
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(0x00ffff) },
        opacity: { value: 0.8 },
        thickness: { value: 0.02 }
      },
      transparent: true,
      side: THREE.BackSide,
      depthTest: false
    });
    
    // Click ripple material
    this.rippleMaterial = new THREE.ShaderMaterial({
      vertexShader: this.getRippleVertexShader(),
      fragmentShader: this.getRippleFragmentShader(),
      uniforms: {
        time: { value: 0 },
        progress: { value: 0 },
        center: { value: new THREE.Vector3() },
        color: { value: new THREE.Color(0xffffff) },
        intensity: { value: 1.0 }
      },
      transparent: true,
      depthTest: false,
      blending: THREE.AdditiveBlending
    });
    
    // Glow material for hover effects
    this.glowMaterial = new THREE.ShaderMaterial({
      vertexShader: this.getGlowVertexShader(),
      fragmentShader: this.getGlowFragmentShader(),
      uniforms: {
        time: { value: 0 },
        intensity: { value: 1.0 },
        color: { value: new THREE.Color(0xffaa00) }
      },
      transparent: true,
      blending: THREE.AdditiveBlending
    });
  }
  
  public handleInteraction(event: InteractionEvent): void {
    switch (event.type) {
      case 'hover':
        this.handleHover(event);
        break;
      case 'click':
        this.handleClick(event);
        break;
      case 'select':
        this.handleSelection(event);
        break;
      case 'camera_transition':
        this.handleCameraTransition(event);
        break;
    }
  }
  
  private handleHover(event: InteractionEvent): void {
    if (!event.object) return;
    
    const objectId = event.object.uuid;
    
    // Remove existing hover effect if any
    this.removeHoverEffect(objectId);
    
    // Create new hover effect
    const hoverEffect: HoverEffect = {
      object: event.object,
      originalScale: event.object.scale.clone(),
      targetScale: event.object.scale.clone().multiplyScalar(1.1),
      startTime: this.time,
      duration: 300 // ms
    };
    
    this.hoverEffects.set(objectId, hoverEffect);
  }
  
  private handleClick(event: InteractionEvent): void {
    if (!event.position) return;
    
    // Create click ripple effect
    const rippleGeometry = new THREE.RingGeometry(0, 1, 16, 1);
    const rippleMesh = new THREE.Mesh(rippleGeometry, this.rippleMaterial.clone());
    
    rippleMesh.position.copy(event.position);
    rippleMesh.lookAt(
      event.position.clone().add(new THREE.Vector3(0, 0, 1))
    );
    
    const ripple: ClickRipple = {
      position: event.position.clone(),
      mesh: rippleMesh,
      startTime: this.time,
      duration: 1000 // ms
    };
    
    this.clickRipples.push(ripple);
    this.scene.add(rippleMesh);
  }
  
  private handleSelection(event: InteractionEvent): void {
    if (!event.object) return;
    
    const objectId = event.object.uuid;
    
    // Remove existing selection outline if any
    this.removeSelectionOutline(objectId);
    
    // Create selection outline
    const geometry = this.createOutlineGeometry(event.object);
    if (!geometry) return;
    
    const outlineMesh = new THREE.Mesh(geometry, this.outlineMaterial.clone());
    outlineMesh.scale.copy(event.object.scale).multiplyScalar(1.05);
    outlineMesh.position.copy(event.object.position);
    outlineMesh.rotation.copy(event.object.rotation);
    
    const outline: SelectionOutline = {
      object: event.object,
      outlineMesh,
      startTime: this.time,
      duration: -1 // persistent until removed
    };
    
    this.selectionOutlines.set(objectId, outline);
    this.scene.add(outlineMesh);
  }
  
  private handleCameraTransition(_event: InteractionEvent): void {
    // Add subtle screen flash or motion blur effect
    // This would integrate with a post-processing pipeline
    console.log('Camera transition effect triggered');
  }
  
  private createOutlineGeometry(object: THREE.Object3D): THREE.BufferGeometry | null {
    let geometry: THREE.BufferGeometry | null = null;
    
    object.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        geometry = child.geometry.clone();
        return;
      }
    });
    
    return geometry;
  }
  
  public removeHoverEffect(objectId: string): void {
    const hoverEffect = this.hoverEffects.get(objectId);
    if (hoverEffect) {
      // Reset scale
      hoverEffect.object.scale.copy(hoverEffect.originalScale);
      this.hoverEffects.delete(objectId);
    }
  }
  
  public removeSelectionOutline(objectId: string): void {
    const _outline = this.selectionOutlines.get(objectId);
    if (_outline) {
      this.scene.remove(_outline.outlineMesh);
      _outline.outlineMesh.geometry.dispose();
      if (_outline.outlineMesh.material instanceof THREE.Material) {
        _outline.outlineMesh.material.dispose();
      }
      this.selectionOutlines.delete(objectId);
    }
  }
  
  public clearAllEffects(): void {
    // Clear hover effects
    this.hoverEffects.forEach((_effect, id) => {
      this.removeHoverEffect(id);
    });
    
    // Clear selection outlines
    this.selectionOutlines.forEach((_outline, id) => {
      this.removeSelectionOutline(id);
    });
    
    // Clear ripples
    this.clickRipples.forEach(ripple => {
      this.scene.remove(ripple.mesh);
      ripple.mesh.geometry.dispose();
      if (ripple.mesh.material instanceof THREE.Material) {
        ripple.mesh.material.dispose();
      }
    });
    this.clickRipples = [];
  }
  
  public setQuality(quality: QualityLevel): void {
    this.quality = quality;
    
    // Adjust effect parameters based on quality
    const thickness = quality === 'low' ? 0.01 : quality === 'ultra' ? 0.03 : 0.02;
    this.outlineMaterial.uniforms['thickness'].value = thickness;
    
    // Enable/disable effects based on quality
    if (quality === 'low') {
      // Disable some effects for performance
      this.rippleMaterial.visible = false;
    } else {
      this.rippleMaterial.visible = true;
    }
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update materials
    this.outlineMaterial.uniforms['time'].value = this.time * 0.001;
    this.rippleMaterial.uniforms['time'].value = this.time * 0.001;
    this.glowMaterial.uniforms['time'].value = this.time * 0.001;
    
    // Update hover effects
    this.updateHoverEffects();
    
    // Update selection outlines
    this.updateSelectionOutlines();
    
    // Update click ripples
    this.updateClickRipples();
  }
  
  private updateHoverEffects(): void {
    this.hoverEffects.forEach((_effect, id) => {
      const elapsed = this.time - _effect.startTime;
      const progress = Math.min(elapsed / _effect.duration, 1.0);
      
      // Smooth scale interpolation
      const scale = _effect.originalScale.clone().lerp(_effect.targetScale, this.easeOutCubic(progress));
      _effect.object.scale.copy(scale);
      
      if (progress >= 1.0) {
        this.hoverEffects.delete(id);
      }
    });
  }
  
  private updateSelectionOutlines(): void {
    this.selectionOutlines.forEach((_outline) => {
      // Update outline position/rotation to match object
      _outline.outlineMesh.position.copy(_outline.object.position);
      _outline.outlineMesh.rotation.copy(_outline.object.rotation);
      _outline.outlineMesh.scale.copy(_outline.object.scale).multiplyScalar(1.05);
      
      // Animate outline opacity
      const elapsed = this.time - _outline.startTime;
      const opacity = 0.5 + 0.3 * Math.sin(elapsed * 0.003);
      (_outline.outlineMesh.material as THREE.ShaderMaterial).uniforms['opacity'].value = opacity;
    });
  }
  
  private updateClickRipples(): void {
    for (let i = this.clickRipples.length - 1; i >= 0; i--) {
      const ripple = this.clickRipples[i];
      const elapsed = this.time - ripple.startTime;
      const progress = elapsed / ripple.duration;
      
      if (progress >= 1.0) {
        // Remove finished ripple
        this.scene.remove(ripple.mesh);
        ripple.mesh.geometry.dispose();
        if (ripple.mesh.material instanceof THREE.Material) {
          ripple.mesh.material.dispose();
        }
        this.clickRipples.splice(i, 1);
      } else {
        // Update ripple animation
        const scale = progress * 5; // Expand ripple
        const opacity = 1.0 - progress; // Fade out
        
        ripple.mesh.scale.setScalar(scale);
        (ripple.mesh.material as THREE.ShaderMaterial).uniforms['progress'].value = progress;
        (ripple.mesh.material as THREE.ShaderMaterial).uniforms['intensity'].value = opacity;
      }
    }
  }
  
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }
  
  // Shader code
  private getOutlineVertexShader(): string {
    return `
      uniform float time;
      uniform float thickness;
      
      void main() {
        vec3 newPosition = position + normal * thickness;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
      }
    `;
  }
  
  private getOutlineFragmentShader(): string {
    return `
      uniform float time;
      uniform vec3 color;
      uniform float opacity;
      
      void main() {
        float pulse = 0.5 + 0.5 * sin(time * 3.0);
        gl_FragColor = vec4(color, opacity * pulse);
      }
    `;
  }
  
  private getRippleVertexShader(): string {
    return `
      void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
  }
  
  private getRippleFragmentShader(): string {
    return `
      uniform float time;
      uniform float progress;
      uniform vec3 color;
      uniform float intensity;
      
      void main() {
        vec2 uv = gl_PointCoord - 0.5;
        float dist = length(uv);
        
        float ring = smoothstep(0.4, 0.5, dist) * smoothstep(0.5, 0.4, dist);
        float alpha = ring * intensity;
        
        gl_FragColor = vec4(color, alpha);
      }
    `;
  }
  
  private getGlowVertexShader(): string {
    return `
      void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
  }
  
  private getGlowFragmentShader(): string {
    return `
      uniform float time;
      uniform float intensity;
      uniform vec3 color;
      
      void main() {
        float glow = 0.5 + 0.5 * sin(time * 2.0);
        gl_FragColor = vec4(color, intensity * glow * 0.3);
      }
    `;
  }
  
  public dispose(): void {
    this.clearAllEffects();
    
    // Dispose materials
    this.outlineMaterial.dispose();
    this.rippleMaterial.dispose();
    this.glowMaterial.dispose();
  }
}